import {
  GiftCardsGiftCard,
  OrdersDtoOrderLineDto,
  OrdersProductRequirementLineModel,
  TAnyValue,
} from "@springtree/eva-services-core";
import classNames from "classnames";
import { useAtomValue, useSetAtom } from "jotai";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { collectInStoreSelected, selectedStoreAtom, storeModalAtom } from "~/atoms/changeStore";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useFavouriteStore } from "~/contexts/favourite-store";
import { useCartAvailability, useCartProduct } from "~/hooks/use-cart";
import { AbstractShop } from "~/shared/shipping/abstract-shop.interface";
import CommonUtils from "~/utils/common-utils";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import CartProductPrice from "../cart/cart-product-price";
import styles from "../cart/cart-product-tile.module.scss";
import DiscountProductTile from "../cart/discount-product-tile";
import ProductInfoGift from "../product/product-info-gift";
import checkoutStyles from "./checkout-products.module.scss";

type Props = {
  line: OrdersDtoOrderLineDto;
  giftCardData?: GiftCardsGiftCard;
  physicalGiftCardRequirement?: OrdersProductRequirementLineModel[];
  showNetPriceWithoutPromo?: boolean;
  hideQty?: boolean;
  store?: AbstractShop;
  onClose?: () => void;
  collectInStoreBanner?: boolean;
  disableCartActions?: boolean;
};

export default function CheckoutProductTile(props: Props) {
  const t = useTranslations();
  const cartAvailability = useCartAvailability();

  const product = props.line.Product!.Properties;
  const primaryImage = product?.product_media
    ? (product.product_media as { primary_image?: TAnyValue }).primary_image
    : undefined;
  const isGiftCard = productUtils.isGiftCard(props.line.Product?.Properties);
  const { preferredStore } = useFavouriteStore();

  const displayName = productUtils.getShadeDisplayName(props.line.Product?.Properties);

  const goccia = productUtils.getProductDroplet((product?.product_media as any)?.media);

  const breakpoint = useBreakpoint();

  const setChangeStoreState = useSetAtom(storeModalAtom);
  const collectInStoreSelectedRef = useAtomValue<boolean>(collectInStoreSelected);
  const storeRef = useAtomValue(selectedStoreAtom);
  const enableCollectInStoreBanner =
    CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_CLICK_AND_COLLECT_REMOVE_PRODUCT) && props.collectInStoreBanner;

  const trackingInfo = useMemo(() => {
    return {
      context: "checkout",
      info: TrackingUtils.mapEcomCartItem(props.line),
    };
  }, [props.line]);
  const { removeAllFromCart } = useCartProduct(
    props.line.ProductID!,
    isGiftCard ? props.line.ID : undefined,
    trackingInfo
  );

  if (!product) {
    return;
  }

  let availabilityDate;
  let isUnavailable;
  const store = props.store || storeRef;

  if (store) {
    const pickUpProduct = store.Products?.find((av) => av.ProductID === product.product_id);
    isUnavailable = !pickUpProduct;
    availabilityDate = pickUpProduct?.AvailabilityDate;
  } else if (!!cartAvailability) {
    const pickUpProduct = cartAvailability.Products?.find((av) => av.ProductID === product.product_id);
    isUnavailable = !pickUpProduct?.Pickup?.IsAvailable;
    if (!isUnavailable) {
      availabilityDate = pickUpProduct?.Pickup?.IsAvailable && pickUpProduct?.Pickup?.AvailabilityDate;
    }
  }
  return (
    <>
      <div>
        <div className={styles.container}>
          <Image
            src={
              props.giftCardData?.Data?.Theme?.ImageUrl ||
              (primaryImage as { url: string } | undefined)?.url ||
              imageUtils.transparentPixel
            }
            alt={typeof product.display_value === "string" ? product.display_value : ""}
            width={80}
            height={80}
          />
          <div className={styles.infoContainer}>
            <p className={styles.productName}>
              {typeof product.display_value === "string" ? product.display_value : ""}
            </p>
            {props.giftCardData || (props.physicalGiftCardRequirement || []).length > 0 ? (
              <ProductInfoGift
                giftCardData={props.giftCardData}
                physicalGiftCardRequirement={props.physicalGiftCardRequirement}
              />
            ) : displayName ? (
              <div className={styles.colorContainer}>
                <Image
                  src={goccia?.url || imageUtils.transparentPixel}
                  alt={displayName}
                  width={12}
                  height={12}
                  className={styles.colorImage}
                  loader={imageUtils.evaImageLoader}
                />
                <p className={styles.colorName}>{displayName}</p>
              </div>
            ) : (
              <div className={styles.colorPlaceholder} />
            )}
            {!props.hideQty && (
              <p className={styles.colorName}>
                {t("generic.quantity", {
                  quantity:
                    (props.line.QuantityOrdered ?? 0) - (props.line.QuantityCancelled ?? 0) ||
                    (props.line.QuantityOpen ?? 0),
                })}
              </p>
            )}
            <div className={checkoutStyles.priceContainer}>
              <CartProductPrice showNetPriceWithoutPromo={props.showNetPriceWithoutPromo} line={props.line} />
            </div>
            {!props.showNetPriceWithoutPromo && props.line.DiscountAmount != 0 && (
              <DiscountProductTile line={props.line} showNetPriceWithoutPromo={props.showNetPriceWithoutPromo} />
            )}
          </div>
        </div>
        {props.showNetPriceWithoutPromo && props.line.DiscountAmount != 0 && (
          <DiscountProductTile line={props.line} showNetPriceWithoutPromo={props.showNetPriceWithoutPromo} />
        )}
      </div>
      {enableCollectInStoreBanner &&
        (collectInStoreSelectedRef || isUnavailable) &&
        (!availabilityDate || new Date(availabilityDate) > new Date()) &&
        !isGiftCard && (
          <div className={styles.stock}>
            <div className={styles.stockStoreInformation}>
              <div className={styles.stockIndicator} style={{ backgroundColor: "#CC0C0C" }} />
              <p className={styles.stockText}>
                {isUnavailable ? (
                  <span>{t("generic.productUnavailableAtAnyLocation")}</span>
                ) : store ? (
                  <>
                    <span>{t("generic.productUnavailableAtThisLocation")}</span>
                    {<strong>{store.Name}</strong>}
                  </>
                ) : preferredStore ? (
                  <>
                    <span>{t("generic.productUnavailableAtThisLocation")}</span>
                    {<strong>{preferredStore?.Name}</strong>}
                  </>
                ) : (
                  <></>
                )}
              </p>
            </div>
            <div className={styles.stockCTAs}>
              {breakpoint === "mobile" && !isUnavailable && (
                <button
                  className={styles.stockButton}
                  onClick={() => {
                    setChangeStoreState(true);
                    if (props.onClose) props.onClose();
                  }}
                >
                  {t(store ? "generic.backToStoreSelection" : "generic.backToShipSelection")}
                </button>
              )}
              <button
                className={classNames(styles.stockButton, styles.stockButtonRemove)}
                onClick={async () => await removeAllFromCart.mutateAsync()}
              >
                {t("generic.remove")}
              </button>
            </div>
          </div>
        )}
    </>
  );
}
